/**
 * Mentor engagement list (WS-5.2).
 *
 * One card per mentee: level, session/SOF counts, CRF status, ICF rule badge.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { WebAppShell } from '@/design-system/components';
import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import { trackAuthEvent } from '@/services/analytics';
import { MentorEngagementsAPI } from '@/services/api/mentorEngagementsAPI';
import type { EngagementCreateRequest, EngagementStatus, EngagementSummary } from '@/types/mentorEngagements';
import { ActionButton, Chip, Panel, SegmentedField, mentorStyles } from '@/components/mentorEngagements/shared';
import { NewEngagementModal } from '@/components/mentorEngagements/NewEngagementModal';
import { cardNextActionKey, loadEngagementList } from '@/components/mentorEngagements/engagementList';
import { MCS_ENABLED } from '@/config/featureFlags';
import { MentorSetupChecklistCard } from '@/components/mentorEngagements/MentorSetupChecklistCard';

type StatusFilter = 'all' | EngagementStatus;
const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'completed', 'archived'];

export default function MentorEngagementListScreen() {
  const router = useRouter();
  // `?new=1`: the setup checklist's "Add your first mentee" opens the create form directly.
  const params = useLocalSearchParams<{ new?: string | string[] }>();
  const openNew = (Array.isArray(params.new) ? params.new[0] : params.new) === '1';
  const { t } = useTranslation();
  const { isPhone, isTablet } = useResponsive();
  const [engagements, setEngagements] = useState<EngagementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createVisible, setCreateVisible] = useState(false);
  // Client-side filter over the single list fetch: no loading flash, card testIDs stay stable.
  const [filter, setFilter] = useState<StatusFilter>('all');

  // One bounded request: the list rows carry each card's overview summary
  // (draft count, next action), so opening Mentees no longer fires one
  // /overview call per engagement. The workspace overview loads on open.
  const loadIdRef = useRef(0);
  const load = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setLoading(true);
    try {
      const rows = await loadEngagementList(MentorEngagementsAPI);
      if (loadId !== loadIdRef.current) return;
      setEngagements(rows);
      setError(null);
    } catch {
      if (loadId === loadIdRef.current) setError(t('mentorEngagements.loadError'));
    } finally {
      if (loadId === loadIdRef.current) setLoading(false);
    }
  }, [t]);

  const visible = useMemo(
    () => (filter === 'all' ? engagements : engagements.filter(engagement => engagement.status === filter)),
    [engagements, filter],
  );
  const counts = useMemo(() => ({
    all: engagements.length,
    active: engagements.filter(e => e.status === 'active').length,
    completed: engagements.filter(e => e.status === 'completed').length,
    archived: engagements.filter(e => e.status === 'archived').length,
  }), [engagements]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openNew) return;
    setCreateVisible(true);
    router.setParams({ new: undefined } as never);
  }, [openNew, router]);

  const handleCreate = async (payload: EngagementCreateRequest) => {
    const created = await MentorEngagementsAPI.create(payload);
    trackAuthEvent('engagement_created', { target_level: payload.target_level, language: payload.language });
    // Open the new workspace right away; the list refreshes behind it.
    router.push({ pathname: ROUTES.MENTOR_ENGAGEMENT_DETAIL, params: { id: String(created.id) } } as never);
    void load();
  };

  const openDetail = (engagementId: number) => {
    router.push({ pathname: ROUTES.MENTOR_ENGAGEMENT_DETAIL, params: { id: String(engagementId) } } as never);
  };

  const body = (
    <ScrollView
      testID="mentor_engagements_screen"
      style={mentorStyles.screen}
      contentContainerStyle={[
        mentorStyles.content,
        isTablet && mentorStyles.contentTablet,
        isPhone && mentorStyles.contentPhone,
      ]}
    >
      <View style={mentorStyles.header}>
        <Text style={[mentorStyles.title, isPhone && mentorStyles.titlePhone]}>
          {t('mentorEngagements.title')}
        </Text>
        <Text style={mentorStyles.subtitle}>{t('mentorEngagements.subtitle')}</Text>
        <MentorSetupChecklistCard surface="mentees" />
        {MCS_ENABLED && <Panel><Pressable accessibilityRole="link" onPress={() => router.push(ROUTES.MCS_PREPARATION as never)}><Text style={[mentorStyles.bodyText, { color: reviewDesk.accent }]}>{t('mcs.linkCard')}</Text></Pressable></Panel>}
        <View style={mentorStyles.row}>
          <ActionButton
            testID="new_engagement_button"
            label={t('mentorEngagements.newEngagement')}
            icon="add"
            variant="primary"
            onPress={() => setCreateVisible(true)}
          />
          {MentorEngagementsAPI.isMockEnabled ? (
            <Chip label={t('mentorEngagements.mockBanner')} tone="warning" icon="science" />
          ) : null}
        </View>
      </View>

      {loading ? (
        <View style={mentorStyles.center}><ActivityIndicator color={reviewDesk.accent} /></View>
      ) : error ? (
        <Panel testID="mentor_engagements_error">
          <Text accessibilityRole="alert" style={mentorStyles.errorText}>{error}</Text>
          <View style={mentorStyles.row}>
            <ActionButton testID="mentor_engagements_retry" icon="refresh" label={t('mentorEngagements.retry')} onPress={() => void load()} />
          </View>
        </Panel>
      ) : engagements.length === 0 ? (
        <Panel testID="mentor_engagements_empty">
          <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.empty')}</Text>
          <Text style={mentorStyles.bodyText}>{t('mentorEngagements.emptyBody')}</Text>
        </Panel>
      ) : (
        <View style={{ gap: 14 }}>
          <SegmentedField<StatusFilter>
            testID="engagement_status_filter"
            label={t('mentorEngagements.filters.label')}
            options={STATUS_FILTERS.map(value => ({ value, label: `${t(`mentorEngagements.filters.${value}`)} (${counts[value]})` }))}
            value={filter}
            onChange={setFilter}
          />
          {visible.length === 0 ? (
            <Panel testID="mentor_engagements_empty_filtered">
              <Text style={mentorStyles.bodyText}>{t('mentorEngagements.filters.emptyFiltered')}</Text>
            </Panel>
          ) : null}
          {visible.map((engagement) => (
            <Pressable
              key={engagement.id}
              testID={`engagement_card_${engagement.id}`}
              onPress={() => openDetail(engagement.id)}
              accessibilityRole="button"
              style={({ pressed }) => [mentorStyles.panel, pressed && mentorStyles.pressed]}
            >
              <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
                <Text style={[mentorStyles.panelTitle, { flexShrink: 1 }]}>{engagement.mentee_name || t('mentorEngagements.privateMentee')}</Text>
                <MaterialIcons name="chevron-right" size={22} color={reviewDesk.muted} />
              </View>
              <View style={mentorStyles.row}>
                <Chip label={engagement.target_level.toUpperCase()} tone="accent" />
                {engagement.status !== 'active' ? (
                  <Chip
                    label={t(`mentorEngagements.status.${engagement.status}`)}
                    tone={engagement.status === 'completed' ? 'success' : 'neutral'}
                    icon={engagement.status === 'archived' ? 'archive' : 'check-circle'}
                  />
                ) : null}
                <Chip label={t('mentorEngagements.list.sessions', { count: engagement.sessions_count })} />
                {typeof engagement.sof_draft_count === 'number' ? <Chip label={t('mentorEngagements.workspace.drafts', { count: engagement.sof_draft_count })} /> : null}
                <Chip
                  label={engagement.crf_status === 'final'
                    ? t('mentorEngagements.list.crfFinal')
                    : t('mentorEngagements.list.crfDraft')}
                  tone={engagement.crf_status === 'final' ? 'success' : 'neutral'}
                />
              </View>
              <Text style={mentorStyles.bodyText}>{cardNextActionKey(engagement)
                ? t(`mentorEngagements.workspace.next.${cardNextActionKey(engagement)}`)
                : t('mentorEngagements.workspace.viewStatus')}</Text>
            </Pressable>
          ))}
        </View>
      )}

      <NewEngagementModal
        visible={createVisible}
        onClose={() => setCreateVisible(false)}
        onCreate={handleCreate}
      />
    </ScrollView>
  );

  return <WebAppShell active="mentor-engagements" contentMaxWidth="100%" scroll={false}>{body}</WebAppShell>;
}
